<template>
  <BContainer
    class="px-0"
    fluid
  >

    <BContainer
      v-if="showUploader"
      class="px-0"
      fluid
    >
      <ValidationObserver
        ref="formDocument"
      >
        <form
          role="form"
          novalidate
          @submit.prevent
        >
          <BRow>
            <BCol
              cols="12"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="attachments"
                name="attachments"
                rules="image|max_attachment:10"
              >
                <BFormGroup
                  label="Attachments"
                  label-for="fAttachments"
                  label-class="font-weight-bolder"
                >
                  <BFormFile
                    id="fAttachments"
                    v-model="attachment.list"
                    :accept="accept"
                    :multiple="multiple"
                    :state="errors.length > 0 ? false : null"
                  />
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>
            <BCol
              v-if="showProgress && upload.progress > 0"
              cols="12"
              class="pb-2"
            >
              <BProgress
                :value="upload.progress"
                striped
                animated
                show-progress
                variant="primary"
              />
            </BCol>
            <BCol
              v-if="showProgress && totalCompressingFiles > 0"
              cols="12"
              class="pb-2"
            >
              <BProgress
                striped
                animated
                show-progress
                variant="primary"
                :value="compressorProgress"
              />
              Compressing Attached Files
            </BCol>
          </BRow>
        </form>
      </ValidationObserver>

      <BRow
        v-if="showCompressed && totalCompressingFiles <= 0"
      >
        <BCol
          cols="12"
          class="mb-2 pb-2 border-bottom"
        >
          <div class="d-flex justify-content-between">
            <div>
              <strong>Compressed</strong> - {{ compressedFiles.length }} Files
            </div>
            <div class="d-flex gap-2">
              <BButton
                size="sm"
                :variant="compressedFiles.length <= 0 ? 'outline-danger' : 'danger'"
                :disabled="compressedFiles.length <= 0 || busy"
                @click.prevent="deleteCompressedAll"
              >
                Delete All
              </BButton>
            </div>
          </div>
        </BCol>
        <BCol
          v-for="(file, key) in compressedFiles"
          :key="key"
          cols="12"
          class="mb-2 px-2"
        >
          <div
            class="d-flex flex-row border rounded h-100 attachment-background"
          >
            <div
              class="p-2"
            >
              <BImg
                :src="toBaseUrl(file)"
                class="attachment"
              />
            </div>
            <div class="d-flex flex-column justify-content-between w-100 h-100 p-2">
              <div class="d-flex flex-column">
                <div class="text-break font-weight-bolder">
                  {{ file.name }}
                </div>
                <div style="font-size: 12px;">
                  {{ Number(file.size / 1000).toFixed(2) }} KB
                </div>
              </div>
              <div class="align-self-end">
                <BButton
                  size="sm"
                  variant="warning"
                  @click.prevent="previewCompress(file, key)"
                >
                  View
                </BButton>
                <BButton
                  v-if="showUploader"
                  size="sm"
                  variant="outline-danger"
                  class="ml-1"
                  @click.prevent="deleteCompressed(file, key)"
                >
                  Delete
                </BButton>
              </div>
            </div>
          </div>
        </BCol>
      </BRow>

      <div class="d-flex justify-content-between border-bottom pb-3 mb-4">
        <div>
          <strong>Uploaded</strong> - {{ uploadedFiles.length }} Files
        </div>
        <div class="d-flex gap-2">
          <BButton
            size="sm"
            :variant="totalCompressingFiles > 0 || compressedFiles.length <= 0 || attachment.list.length > 10 ? 'outline-primary' : 'primary'"
            :disabled="totalCompressingFiles > 0 || compressedFiles.length <= 0 || attachment.list.length > 10 || busy"
            @click.prevent="uploadDocuments"
          >
            Upload
          </BButton>
          <BButton
            size="sm"
            :variant="uploadedFiles.length <= 0 ? 'outline-danger' : 'danger'"
            :disabled="uploadedFiles.length <= 0 || busy"
            @click.prevent="deleteUploadedAll"
          >
            Delete All
          </BButton>
        </div>
      </div>
    </BContainer>
    <BContainer
      v-else-if="!showUploader && uploadedFiles.length <= 0"
      class="px-0"
      fluid
    >
      <div class="d-flex flex-column justify-content-center align-items-center min-h-300px">
        <BImg
          :src="getInvalidImageUrl"
          width="250px"
        />
        NO ATTACHMENTS
      </div>
    </BContainer>

    <BRow>
      <BCol
        v-for="(file, key) in uploadedFiles"
        :key="key"
        cols="12"
        class="mb-2"
      >
        <div
          class="d-flex flex-row border rounded h-100 attachment-background"
        >
          <div
            class="p-2"
          >
            <BImg
              :src="uploadedDocumentEndpoint(file.file_hashed_name)"
              class="attachment"
            />
          </div>
          <div class="d-flex flex-column justify-content-between w-100 h-100 p-2">
            <div class="d-flex flex-column">
              <div class="text-break font-weight-bolder">
                {{ file.file_original_name }}
              </div>
              <div style="font-size: 12px;">
                {{ Number(file.file_size / 1000).toFixed(2) }} KB
              </div>
            </div>
            <div class="align-self-end">
              <BButton
                size="sm"
                variant="warning"
                @click.prevent="previewDocument(file, key)"
              >
                View
              </BButton>
              <BButton
                v-if="showUploader"
                size="sm"
                variant="outline-danger"
                class="ml-1"
                @click.prevent="deleteUploaded(file, key)"
              >
                Delete
              </BButton>
            </div>
          </div>
        </div>
      </BCol>
    </BRow>

    <BModal
      ref="modalDocuments"
      size="xl"
      scrollable
      hide-header
      no-close-on-backdrop
      modal-class="full-screen"
    >
      <BCarousel
        id="carouselDocuments"
        v-model="carousel"
        controls
        indicators
        no-touch
        class="h-100"
        background="#aaa"
        :interval="1000000"
      >
        <template
          v-if="preview.compress"
        >
          <BCarouselSlide
            v-for="(file, key) in compressedFiles"
            :key="`compressed-${key}`"
            caption-tag="h6"
            :caption="file.name"
            :text="`${Number(file.size / 1000).toFixed(2)} KB`"
            style="min-height: calc(100vh - 85px);"
          >
            <template #img>
              <div
                class="d-flex justify-content-center align-items-center"
              >
                <panZoom
                  :options="{
                    minZoom: 0.5,
                    maxZoom: 5,
                    bounds: true,
                    boundsPadding: 0.5
                  }"
                >
                  <BImg
                    class="d-blocks"
                    :src="toBaseUrl(file)"
                  />
                </panZoom>
              </div>
            </template>
          </BCarouselSlide>
        </template>
        <template
          v-else
        >
          <BCarouselSlide
            v-for="(file, key) in uploadedFiles"
            :key="`uploaded-${key}`"
            caption-tag="h6"
            :caption="file.file_original_name"
            :text="`${Number(file.file_size / 1000).toFixed(2)} KB`"
            style="min-height: calc(100vh - 85px);"
          >
            <template #img>
              <div
                class="d-flex justify-content-center align-items-center"
              >
                <panZoom
                  :options="{
                    minZoom: 0.5,
                    maxZoom: 5,
                    bounds: true,
                    boundsPadding: 0.5
                  }"
                >
                  <BImg
                    class="d-blocks"
                    :src="uploadedDocumentEndpoint(file.file_hashed_name)"
                  />
                </panZoom>
              </div>
            </template>
          </BCarouselSlide>
        </template>
      </BCarousel>
      <template #modal-footer="{ cancel }">
        <div class="col-12 d-flex justify-content-between px-0 pb-0 mb-0">
          <div />
          <div>
            <BButton

              variant="outline-dark"
              :disabled="busy"
              @click.prevent="discardBtnEvent => preventAriaHidden(discardBtnEvent, cancel)"
            >
              Close <span class="d-none d-md-inline-block">Preview</span>
            </BButton>
          </div>
        </div>
      </template>
    </BModal>
  </BContainer>
</template>
<script>
import QUERY from 'lodash'
import imageCompressor from 'browser-image-compression'

import MxMisc from '@/mixins/misc'

import { SSharedCustomerDocument } from '@/services/shared'

export default {
  name: 'VRSMCustomerDocumentUploader',

  mixins: [
    MxMisc
  ],

  props: {
    accept: {
      type: String,
      default: () => 'image/jpg, image/jpeg, image/png' // 'image/*' // 'image/jpg, image/jpeg, image/png', application/pdf
    },
    multiple: {
      type: Boolean,
      default: () => true
    },
    showProgress: {
      type: Boolean,
      default: () => true
    },
    showUploader: {
      type: Boolean,
      default: () => true
    },
    showCompressed: {
      type: Boolean,
      default: () => false
    },
    path: {
      type: String,
      required: true,
      default: () => ''
    },
    files: {
      type: Array,
      required: true,
      default: () => []
    }
  },

  data () {
    return {
      preview: {
        compress: true
      },
      busy: false,
      carousel: 0,
      upload: {
        progress: 0,
        loaded: 0,
        total: 0
      },
      attachment: {
        list: [],
        path: this.path
      },
      invalidFiles: [],
      compressingFiles: [],
      compressedFiles: [],
      uploadedFiles: this.files
    }
  },

  computed: {

    getInvalidImageUrl () {
      return `${window.location.origin}/img/invalid-image.png`
    },

    compressorProgress () {
      const totalProgress = this.compressingFiles.map(compressor => compressor.progress).reduce(
        (total, amount) => Number(total) + Number(amount), 0
      ) / 100

      return totalProgress / this.compressingFiles.length * 100
    },

    totalCompressingFiles () {
      return this.compressingFiles.filter(file => file.compressing).length
    }
  },

  watch: {
    'attachment.list' (files) {
      if (!this.showUploader) {
        this.compressedFiles = []
      }

      this.compressFiles({ target: { files } })
    }
  },

  methods: {

    toBaseUrl (file) {
      window.URL = window.URL || window.webkitURL
      return window.URL.createObjectURL(file)
    },

    async compressFiles ({ target }) {
      if (Array.from(target.files).length >= 10) {
        return ''
      }

      const isValidImage = ['image/jpg', 'image/jpeg', 'image/png']

      this.invalidFiles = QUERY.remove(this.attachment.list, file => {
        const isAlreadyCompressed = Array.from(this.compressedFiles).some(
          compressedFile => String(file.name).toLowerCase().replace(/\s/g, '') === String(compressedFile.name).toLowerCase().replace(/\s/g, '')
        )

        const isAlreadyUploaded = Array.from(this.uploadedFiles).some(
          uploadedFile => String(file.name).toLowerCase().replace(/\s/g, '') === String(uploadedFile.file_original_name).toLowerCase().replace(/\s/g, '')
        )

        const isInvalidFileType = !isValidImage.includes(file.type)

        return isInvalidFileType || isAlreadyCompressed || isAlreadyUploaded
      })

      this.compressingFiles = Array.from(this.attachment.list).map(
        unCompressedFile => ({
          file: unCompressedFile,
          progress: 0,
          compressed: false,
          compressing: false
        })
      )

      Array.from(this.compressingFiles).forEach(
        compressor => {
          compressor.compressing = true
          imageCompressor(compressor.file, {
            // maxIteration: 10,
            maxSizeMB: 0.15,
            useWebWorker: true,
            maxWidthOrHeight: 1920,
            onProgress: progress => {
              compressor.progress = progress
            }
          }).then(
            compressedFile => {
              this.compressedFiles.push(compressedFile)
              compressor.compressed = true
            }
          ).catch(() => {
            this.invalidFiles.push(compressor.file)
            compressor.compressed = false
          }).finally(() => {
            compressor.compressing = false
          })
        }
      )
    },

    uploadedDocumentEndpoint (fileName) {
      if (this.showUploader) {
        return `${process.env.VUE_APP_API_BASE_URL}/customer-memos-uploads/${this.attachment.path}/${fileName}`
      }
      return `${process.env.VUE_APP_API_BASE_URL}/${this.attachment.path}/${fileName}`
    },

    async uploadDocuments () {
      this.busy = true

      const formDocument = new Promise(resolve => {
        const isDocumentPathValid = [null, 'null', ''].includes(this.attachment.path)
        let totalSize = this.uploadedFiles.map(file => file.file_size).reduce(
          (totalSize, fileSize) => parseFloat(totalSize) + parseFloat(fileSize), 0
        )

        const form = new FormData()
        form.append('attachment_path', isDocumentPathValid ? '' : this.attachment.path)

        this.compressedFiles.forEach(
          (compressedFile, position, lists) => {
            totalSize += parseFloat(compressedFile.size)

            form.append(`files[${position}]`, compressedFile, compressedFile.name)

            if ((Number(position) + 1) === lists.length) {
              resolve({ form, totalSize })
            }
          }
        )
      })

      return formDocument.then(
        ({ form, totalSize }) => {
          const oneMegaBytes = 1000000
          const errorMessage = 'The attachment total size must be at least lesser than or equal to 25MB'

          return new Promise((resolve, reject) => {
            if (parseFloat(totalSize) > parseFloat(oneMegaBytes * 25)) {
              return reject(new Error({
                attachments: [errorMessage]
              }))
            }

            SSharedCustomerDocument.uploadFiles(
              form,
              (progress, loaded, total) => {
                this.upload.progress = progress
                this.upload.loaded = loaded
                this.upload.total = total
              }
            ).then(
              ({ data }) => {
                this.$refs.formDocument.reset()
                this.attachment.list = this.compressedFiles = this.compressingFiles = []
                this.attachment.path = data.attachment_path
                this.uploadedFiles.push(...data.attachments)
                this.onUploaded()
              }
            ).catch(
              ({ message }) => reject(message.errors)
            ).finally(() => {
              resolve()
              this.upload.progress = 0
              this.upload.loaded = 0
              this.upload.total = 0
              this.busy = false
            })
          })
        }
      ).catch(errors => {
        this.$refs.formDocument.setErrors(errors)
      }).finally(() => {
        this.busy = false
      })
    },

    async previewCompress (_attachment, position) {
      this.preview.compress = true
      this.carousel = position
      this.$refs.modalDocuments.show()
    },

    async previewDocument (_attachment, position) {
      this.preview.compress = false
      this.carousel = position
      this.$refs.modalDocuments.show()
    },

    async deleteCompressed (file, position) {
      this.swalConfirm({
        html: (
          `<div>
            <h6>Delete this Attachment?</h6>
            <h6 class="mt-2">
              <span class="font-weight-bolder text-dark">
                ${file.name}
              </span>
            </h6>
          </div>`
        ),
        confirmButtonText: 'Delete',
        customClass: {
          confirmButton: 'btn btn-sm btn-danger'
        },
        preConfirm: async () => {
          return this.swalSuccess('Compressed Attachment has been Deleted').then(() => {
            this.compressedFiles.splice(position, 1)
          })
        }
      })
    },

    async deleteCompressedAll () {
      this.swalConfirm({
        html: '<h6>Delete all the Compressed Attachments?</h6>',
        confirmButtonText: 'Delete All',
        customClass: {
          confirmButton: 'btn btn-sm btn-danger'
        },
        preConfirm: async () => {
          return this.swalSuccess('All Compressed Attachments has been Deleted').then(() => {
            this.compressedFiles = this.attachment.list = []
          })
        }
      })
    },

    async deleteUploaded (attachment) {
      this.swalConfirm({
        html: (
          `<div>
            <h6>Delete this Attachment?</h6>
            <h6 class="mt-2">
              <span class="font-weight-bolder text-dark">
                ${attachment.file_original_name}
              </span>
            </h6>
          </div>`
        ),
        confirmButtonText: 'Delete',
        customClass: {
          confirmButton: 'btn btn-sm btn-danger'
        },
        preConfirm: async () => {
          this.busy = true
          return await SSharedCustomerDocument.removeFile({
            attachment_path: this.attachment.path,
            file_name: attachment.file_hashed_name
          }).then(
            ({ data: { message } }) => {
              this.$refs.formDocument.reset()
              this.swalSuccess(message).then(() => {
                this.uploadedFiles = this.uploadedFiles.filter(
                  file => file.file_hashed_name !== attachment.file_hashed_name
                )
                this.onUploaded()
              })
              return message
            }
          ).finally(() => {
            this.busy = false
          })
        }
      })
    },

    async deleteUploadedAll () {
      this.swalConfirm({
        html: '<h6>Delete all the Uploaded Attachments?</h6>',
        confirmButtonText: 'Delete All',
        customClass: {
          confirmButton: 'btn btn-sm btn-danger'
        },
        preConfirm: async () => {
          this.busy = true
          return await SSharedCustomerDocument.removeFiles({
            attachment_path: this.attachment.path
          }).then(
            ({ data: { message } }) => {
              this.$refs.formDocument.reset()
              this.swalSuccess(message).then(() => {
                this.uploadedFiles = []
                this.onUploaded()
              })
              return message
            }
          ).finally(() => {
            this.busy = false
          })
        }
      })
    },

    onUploaded () {
      this.$emit('onUploaded', {
        attachment_path: this.attachment.path,
        attachments: this.uploadedFiles
      })
    }

  }
}
</script>
<style lang="scss">
.attachment-background {
 background-color: var(--iq-body-bg);
}

.dark .attachment-background {
 background-color: var(--iq-dark-body-bg);
}

.carousel-control-next:hover, .carousel-control-prev:hover {
  background-color: #888;
}

img.attachment {
  width: 75px !important;
  height: 75px !important;
  object-fit: contain !important;
  border-radius: 8px;
  border: 1px solid gray !important;
  background-color: #aaa !important;
}

div.attachment {
  width: 75px !important;
  height: 75px !important;
  padding: 4px !important;
  position: relative !important;

  iframe.attachment-frame {
    width: 75px !important;
    height: 75px !important;
    border-radius: 8px;
    border: 1px solid gray !important;
    background-color: #aaa !important;
  }
}

.carousel-item {
  div.d-flex.justify-content-center.align-items-center {
    min-height: calc(100vh - 85px);
    max-height: calc(100vh - 85px);
    // min-width: 100vw;
    max-width: 100vw;
    transition: transform 0.3s ease-in-out;

    iframe {
      min-height: calc(100vh - 85px);
      max-height: calc(100vh - 85px);
      min-width: 68vw;
      max-width: 68vw;
    }

    img.d-blocks {
      min-height: calc(100vh - 85px);
      max-height: calc(100vh - 85px);
      min-width: 68vw;
      max-width: 68vw;
      object-fit: contain;
      position: relative;
    }
  }
}

.progress > .progress-bar.bg-primary {
    background-image: linear-gradient(
      45deg,
    rgba(255, 255, 255, 0.15) 25%,
      transparent 25%,
      transparent 50%,
    rgba(255, 255, 255, 0.15) 50%,
    rgba(255, 255, 255, 0.15) 75%,
      transparent 75%,
      transparent
    );
    background-size: 1rem 1rem;
}
</style>
